import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
	LayoutDashboard,
	Sparkles,
	Activity,
	BookOpen,
	Bot,
	Lock,
	CreditCard,
	Settings2,
	Shield,
	LogOut,
	Menu,
	X,
	Bell,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import { NAV, APP } from '@/constants/testIds';

const NAV_ITEMS = [
	{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: NAV.dashboard },
	{ to: '/assessment', label: 'Assessment', icon: Sparkles, testId: NAV.assessment },
	{ to: '/progress', label: 'Progress', icon: Activity, testId: NAV.progress },
	{ to: '/coach', label: 'Family Coach', icon: Bot, testId: NAV.coach },
	{ to: '/library', label: 'Library', icon: BookOpen, testId: NAV.library },
	{ to: '/vault', label: 'Vault', icon: Lock, testId: NAV.vault, premium: true },
	{ to: '/subscription', label: 'Plan', icon: CreditCard, testId: NAV.subscription },
];

export default function Layout({ children }) {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = useState(false);

	const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
	const isSuper = user?.role === 'SUPER_ADMIN';

	const handleLogout = async () => {
		await logout();
		navigate('/login');
	};

	return (
		<div className="min-h-screen flex bg-[#F8FAFC]">
			{/* Sidebar (desktop) */}
			<aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
				<div className="px-6 pt-6 pb-4">
					<Link to="/dashboard"><Logo /></Link>
				</div>
				<nav className="flex-1 px-3 py-2 space-y-1">
					{NAV_ITEMS.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							data-testid={item.testId}
							className={({ isActive }) =>
								`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all ${
									isActive
										? 'bg-emerald-50 text-emerald-700 shadow-sm'
										: 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
								}`
							}
						>
							<item.icon className="w-4 h-4" />
							<span>{item.label}</span>
							{item.premium && user?.plan !== 'PREMIUM' && user?.role !== 'SUPER_ADMIN' && user?.role !== 'ADMIN' && (
								<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Pro</span>
							)}
						</NavLink>
					))}
					{isAdmin && (
						<div className="pt-4 mt-4 border-t border-slate-100">
							<div className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Admin</div>
							<NavLink
								to="/admin"
								data-testid={NAV.admin}
								className={({ isActive }) =>
									`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all ${
										isActive ? 'bg-blue-50 text-blue-800' : 'text-slate-600 hover:bg-slate-50'
									}`
								}
							>
								<Settings2 className="w-4 h-4" /> Admin Console
							</NavLink>
							{isSuper && (
								<NavLink
									to="/super-admin"
									data-testid={NAV.superAdmin}
									className={({ isActive }) =>
										`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all ${
											isActive ? 'bg-blue-50 text-blue-800' : 'text-slate-600 hover:bg-slate-50'
										}`
									}
								>
									<Shield className="w-4 h-4" /> Super Admin
								</NavLink>
							)}
						</div>
					)}
				</nav>
				<div className="p-4 border-t border-slate-100">
					<div className="flex items-center gap-3 px-2 py-2 rounded-2xl bg-slate-50">
						<div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-700 to-emerald-500 grid place-items-center text-white font-bold text-sm">
							{(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}
						</div>
						<div className="min-w-0 flex-1">
							<div className="text-sm font-semibold text-slate-900 truncate">{user?.name || 'Family'}</div>
							<div className="text-xs text-slate-500 truncate">{user?.email || user?.mobile}</div>
						</div>
					</div>
					<button
						type="button"
						onClick={handleLogout}
						data-testid={APP.logoutButton}
						className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-100"
					>
						<LogOut className="w-4 h-4" /> Sign out
					</button>
				</div>
			</aside>

			{/* Mobile drawer */}
			{open && (
				<div className="lg:hidden fixed inset-0 z-40">
					<div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
					<aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl flex flex-col">
						<div className="px-5 pt-5 pb-3 flex items-center justify-between">
							<Logo />
							<button type="button" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close menu">
								<X className="w-5 h-5" />
							</button>
						</div>
						<nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
							{NAV_ITEMS.map((item) => (
								<NavLink
									key={item.to}
									to={item.to}
									onClick={() => setOpen(false)}
									className={({ isActive }) =>
										`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium ${
											isActive ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600'
										}`
									}
								>
									<item.icon className="w-4 h-4" /> {item.label}
								</NavLink>
							))}
							{isAdmin && (
								<NavLink to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-slate-700">
									<Settings2 className="w-4 h-4" /> Admin
								</NavLink>
							)}
							{isSuper && (
								<NavLink to="/super-admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium text-slate-700">
									<Shield className="w-4 h-4" /> Super Admin
								</NavLink>
							)}
						</nav>
						<div className="p-4 border-t">
							<button type="button" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-100">
								<LogOut className="w-4 h-4" /> Sign out
							</button>
						</div>
					</aside>
				</div>
			)}

			<div className="flex-1 min-w-0 flex flex-col">
				{/* Top bar (mobile + tablet) */}
				<header className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200 flex items-center justify-between px-4 py-3">
					<button type="button" onClick={() => setOpen(true)} aria-label="Open menu" className="p-2 rounded-full hover:bg-slate-100">
						<Menu className="w-5 h-5" />
					</button>
					<Link to="/dashboard"><Logo size="sm" /></Link>
					<Link to="/notifications" data-testid={NAV.notifications} className="p-2 rounded-full hover:bg-slate-100">
						<Bell className="w-5 h-5 text-slate-600" />
					</Link>
				</header>

				{/* Page content */}
				<main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-7xl w-full mx-auto" key={location.pathname}>
					{children}
					<Footer />
				</main>
			</div>
		</div>
	);
}
